import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Authenticated from '../../layout/Authenticated';
import { Plus, Search, Eye, Edit2, Filter, Calendar, IndianRupee, FileText, Clock, CheckCircle2, XCircle, RefreshCcw, RotateCcw, Package, AlertCircle, ChevronDown, User, Phone, Receipt, TrendingDown, Activity, ChevronUp, ArrowLeftRight } from 'lucide-react';
import axiosInstance from '../../api/axiosInstance';

const SalesReturnsList = () => {
    const navigate = useNavigate();
    const [salesReturns, setSalesReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReturns, setTotalReturns] = useState(0);
    const [filters, setFilters] = useState({
        returnstatus: '',
        customermobile: '',
        startdate: '',
        enddate: ''
    });
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        fetchSalesReturns();
    }, [currentPage, filters]);

    const fetchSalesReturns = async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: 20,
                return_status: filters.returnstatus || undefined,
                customer_mobile: filters.customermobile || undefined,
                startdate: filters.startdate || undefined,
                enddate: filters.enddate || undefined
            };

            // Remove undefined values
            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const response = await axiosInstance.get('/sales-returns', { params });
            setSalesReturns(response.data.salesReturns);
            setTotalPages(Math.ceil(response.data.total / 20));
            setTotalReturns(response.data.total);
        } catch (error) {
            console.error('Error fetching sales returns:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            returnstatus: '',
            customermobile: '',
            startdate: '',
            enddate: ''
        });
        setCurrentPage(1);
    };

    const getStatusConfig = (status) => {
        const configs = {
            PENDING: { 
                bg: 'bg-amber-50', 
                text: 'text-amber-700', 
                border: 'border-amber-200',
                icon: Clock,
                dot: 'bg-amber-500'
            },
            APPROVED: { 
                bg: 'bg-emerald-50', 
                text: 'text-emerald-700', 
                border: 'border-emerald-200',
                icon: CheckCircle2,
                dot: 'bg-emerald-500'
            },
            REJECTED: { 
                bg: 'bg-rose-50', 
                text: 'text-rose-700', 
                border: 'border-rose-200',
                icon: XCircle,
                dot: 'bg-rose-500'
            },
            PROCESSED: { 
                bg: 'bg-blue-50', 
                text: 'text-blue-700', 
                border: 'border-blue-200',
                icon: Activity,
                dot: 'bg-blue-500'
            },
            REFUNDED: { 
                bg: 'bg-teal-50', 
                text: 'text-teal-700', 
                border: 'border-teal-200',
                icon: RefreshCcw,
                dot: 'bg-teal-500'
            }
        };
        return configs[status] || configs.PENDING;
    };

    const getReasonConfig = (reason) => {
        const configs = {
            DEFECTIVE: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
            EXPIRED: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
            WRONG_MEDICINE: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
            SIDE_EFFECTS: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
            DOCTOR_CHANGE: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
            DUPLICATE_PURCHASE: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
            OTHER: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' }
        };
        return configs[reason] || configs.OTHER;
    };

    const filteredReturns = salesReturns.filter(returnItem =>
        returnItem.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        returnItem.customer_mobile?.includes(searchTerm) ||
        returnItem.id.toString().includes(searchTerm)
    );

    return (
        <Authenticated>
            <div className="min-h-screen">
                <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
                    <div className="mb-8">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                            <div className="flex items-start space-x-4">
                                <div className="bg-teal-600 p-3 rounded-2xl">
                                    <ArrowLeftRight className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900 mb-1">Sales Returns</h1>
                                    <p className="text-gray-600 flex items-center gap-2">
                                        <Package size={16} className="text-teal-600" />
                                        Manage customer returns & refunds
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/new-sales-return')}
                                className="inline-flex items-center justify-center px-6 py-3 bg-teal-600 text-white rounded-xl transition-all duration-200 font-medium"
                            >
                                <Plus size={20} className="mr-2" />
                                New Return
                            </button>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Total Returns</p>
                                        <p className="text-2xl font-bold text-gray-900">{totalReturns}</p>
                                    </div>
                                    <div className="bg-teal-50 p-3 rounded-lg">
                                        <TrendingDown className="text-teal-600" size={24} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Pending</p>
                                        <p className="text-2xl font-bold text-amber-600">
                                            {salesReturns.filter(r => r.return_status === 'PENDING').length}
                                        </p>
                                    </div>
                                    <div className="bg-amber-50 p-3 rounded-lg">
                                        <Clock className="text-amber-600" size={24} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Approved</p>
                                        <p className="text-2xl font-bold text-emerald-600">
                                            {salesReturns.filter(r => r.return_status === 'APPROVED').length}
                                        </p>
                                    </div>
                                    <div className="bg-emerald-50 p-3 rounded-lg">
                                        <CheckCircle2 className="text-emerald-600" size={24} />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Refunded</p>
                                        <p className="text-2xl font-bold text-teal-600">
                                            {salesReturns.filter(r => r.return_status === 'REFUNDED').length}
                                        </p>
                                    </div>
                                    <div className="bg-teal-50 p-3 rounded-lg">
                                        <RefreshCcw className="text-teal-600" size={24} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Search and Filters Section */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
                        <div className="p-6">
                            <div className="flex flex-col lg:flex-row gap-4">
                                {/* Search Bar */}
                                <div className="flex-1">
                                    <div className="relative">
                                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="text"
                                            placeholder="Search by customer name, mobile or return ID..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors"
                                        />
                                    </div>
                                </div>

                                {/* Filter Toggle Button */}
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className={`inline-flex items-center justify-center px-6 py-3 border rounded-xl font-medium transition-all ${
                                        showFilters 
                                            ? 'bg-teal-50 border-teal-200 text-teal-700' 
                                            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <Filter size={18} className="mr-2" />
                                    Filters
                                    {showFilters ? <ChevronUp size={18} className="ml-2" /> : <ChevronDown size={18} className="ml-2" />}
                                </button>
                            </div>

                            {/* Filters Panel */}
                            {showFilters && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Return Status</label>
                                            <select
                                                value={filters.returnstatus}
                                                onChange={(e) => handleFilterChange('returnstatus', e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="PENDING">Pending</option>
                                                <option value="APPROVED">Approved</option>
                                                <option value="REJECTED">Rejected</option>
                                                <option value="PROCESSED">Processed</option>
                                                <option value="REFUNDED">Refunded</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Mobile</label>
                                            <div className="relative">
                                                <Phone size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                                <input
                                                    type="text"
                                                    value={filters.customermobile}
                                                    onChange={(e) => handleFilterChange('customermobile', e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                                    placeholder="Mobile number"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                                            <input
                                                type="date"
                                                value={filters.startdate}
                                                onChange={(e) => handleFilterChange('startdate', e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                                            <input
                                                type="date"
                                                value={filters.enddate}
                                                onChange={(e) => handleFilterChange('enddate', e.target.value)}
                                                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end mt-4">
                                        <button
                                            onClick={clearFilters}
                                            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Returns Table */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="relative">
                                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
                                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-600 border-t-transparent absolute top-0 left-0"></div>
                                </div>
                                <p className="mt-4 text-gray-600 font-medium">Loading returns...</p>
                            </div>
                        ) : filteredReturns.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="bg-teal-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <RotateCcw className="h-10 w-10 text-teal-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">No returns found</h3>
                                <p className="text-gray-500 mb-6">Start processing returns to see them here</p>
                                <button
                                    onClick={() => navigate('/new-sales-return')}
                                    className="inline-flex items-center px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                                >
                                    <Plus size={18} className="mr-2" />
                                    Process First Return
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-teal-600">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Return Info
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Customer
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Original Invoice
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Return Date
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Amount
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Status
                                                </th>
                                                <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">
                                                    Items
                                                </th>
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-white uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-100">
                                            {filteredReturns.map((returnItem) => {
                                                const statusConfig = getStatusConfig(returnItem.return_status);
                                                const reasonConfig = getReasonConfig(returnItem.return_reason);
                                                const StatusIcon = statusConfig.icon;

                                                return (
                                                    <tr key={returnItem.id} className="hover:bg-teal-50/30 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-start space-x-3">
                                                                <div className="bg-teal-600 p-2.5 rounded-lg shadow-sm">
                                                                    <RotateCcw size={18} className="text-white" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-semibold text-gray-900 mb-1">
                                                                        Return #{returnItem.id}
                                                                    </div>
                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${reasonConfig.bg} ${reasonConfig.text} ${reasonConfig.border}`}>
                                                                        {returnItem.return_reason.replace('_', ' ')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="bg-gray-100 p-2 rounded-lg">
                                                                    <User size={16} className="text-gray-600" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {returnItem.customer_name}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 flex items-center mt-0.5">
                                                                        <Phone size={12} className="mr-1" />
                                                                        {returnItem.customer_mobile}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-2">
                                                                <Receipt size={16} className="text-teal-600" />
                                                                <div>
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        #{returnItem.original_invoice_id}
                                                                    </div>
                                                                    {returnItem.originalinvoice && (
                                                                        <div className="text-xs text-gray-500">
                                                                            {new Date(returnItem.originalinvoice.invoice_date).toLocaleDateString('en-GB')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                                                                <Calendar size={16} className="text-gray-400" />
                                                                <span>{new Date(returnItem.return_date).toLocaleDateString('en-GB')}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div>
                                                                <div className="flex items-center text-sm font-semibold text-gray-900">
                                                                    <IndianRupee size={14} className="text-gray-500 mr-0.5" />
                                                                    {parseFloat(returnItem.total_return_amt).toFixed(2)}
                                                                </div>
                                                                {parseFloat(returnItem.refund_amount) > 0 && (
                                                                    <div className="flex items-center text-xs text-emerald-600 mt-1 font-medium">
                                                                        <CheckCircle2 size={12} className="mr-1" />
                                                                        Refunded: ₹{parseFloat(returnItem.refund_amount).toFixed(2)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} mr-2`}></span>
                                                                <StatusIcon size={14} className="mr-1.5" />
                                                                {returnItem.return_status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                                                                <div className="bg-blue-50 p-1.5 rounded">
                                                                    <Package size={14} className="text-blue-600" />
                                                                </div>
                                                                <span className="font-medium">
                                                                    {returnItem.returnitems?.length || 0} items
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center justify-end space-x-2">
                                                                <button
                                                                    onClick={() => navigate(`/sales-returns/${returnItem.id}`)}
                                                                    className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={18} />
                                                                </button>
                                                                {returnItem.return_status === 'PENDING' && (
                                                                    <button
                                                                        onClick={() => navigate(`/sales-returns/${returnItem.id}/status`)}
                                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                        title="Update Status"
                                                                    >
                                                                        <Edit2 size={18} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                            <div className="text-sm text-gray-600">
                                                Showing <span className="font-medium text-gray-900">{((currentPage - 1) * 20) + 1}</span> to{' '}
                                                <span className="font-medium text-gray-900">{Math.min(currentPage * 20, totalReturns)}</span> of{' '}
                                                <span className="font-medium text-gray-900">{totalReturns}</span> returns
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                                                >
                                                    Previous
                                                </button>
                                                <div className="flex space-x-1">
                                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                        const page = i + Math.max(1, currentPage - 2);
                                                        if (page <= totalPages) {
                                                            return (
                                                                <button
                                                                    key={page}
                                                                    onClick={() => setCurrentPage(page)}
                                                                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                                                        currentPage === page
                                                                            ? 'bg-teal-600 text-white shadow-md'
                                                                            : 'border border-gray-300 hover:bg-white'
                                                                    }`}
                                                                >
                                                                    {page}
                                                                </button>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </div>
                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages}
                                                    className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Authenticated>
    );
};

export default SalesReturnsList;